import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import ItemCharts from "./graph/ItemCharts";
import { toFormatPrice } from "../helpers/function";

function mapRows(apiData = []) {
  return apiData.map((row) => ({
    item_name: row.item_name,
    sales: row.sales,
    sellingPrice: row.selling_price,
    qty: row.qty,
  }));
}

function mapTotals(apiTotals = {}) {
  return {
    sales: apiTotals.sales,
    qty: apiTotals.qty,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Item", value: formatNum(summary.total_items) },
    { label: "Qty", value: formatNum(summary.total_qty) },
    { label: "Sales Amount", value: toFormatPrice(Number(summary.total_sales || 0)) },
    {
      label: "Average Sale per Item",
      value: toFormatPrice(Number(summary.avg_sale_per_item || 0)),
    },
  ];
}

const ITEM_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "item_name", label: "Item", align: "left", width: 170 },
  { key: "sales", label: "Sales", align: "left", width: 170, isPriceColumn: true },
  { key: "qty", label: "Qty", align: "left", width: 120 },
];

const DEFAULT_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};
const CHART_MAX_LIMIT = 100000;

const Item = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showCharts, setShowCharts] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);

  const lastQueryRef = useRef({});
  const isFirstFetchRef = useRef(true);

  const fetchSalesByItem = (params = {}) => {
    const merged = { ...lastQueryRef.current, ...params };
    lastQueryRef.current = merged;

    if (isFirstFetchRef.current) setLoading(true);

    Apiauth(merged, "Inventory/reports/salesByItem")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals));
          setMetrics(mapMetrics(res.summary));
          setFilters(res.filters ?? {});
          setPagination(res.pagination ?? DEFAULT_PAGINATION);
        }
      })
      .finally(() => {
        setLoading(false);
        isFirstFetchRef.current = false;
      });
  };

  const fetchChartReport = (activeFilters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...activeFilters },
      "Inventory/reports/salesByItem",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSalesByItem({ page: 1, limit: 10 });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  return (
    <>
      <Header title="Sales by Item" metrics={metrics} />
      <SubHeader
        filters={filters}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={(query) => {
          setAppliedFilters(query);
          fetchSalesByItem({
            ...query,
            page: 1,
            limit: pagination.limit || 10,
          });
        }}
      />
      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <ItemCharts rows={chartRows} />
      ) : (
        <DataTable
          columns={ITEM_COLUMNS}
          data={rows}
          totals={totals}
          renderCell={(col, row) => {
            if (col.key === "sales") return toFormatPrice(Number(row.sales || 0));
            return undefined;
          }}
          fetchData={fetchSalesByItem}
          pageLimit={pagination.limit}
          totalRecords={pagination.total}
          currentPage={pagination.page}
        />
      )}
    </>
  );
};

export default Item;